/**
 * ProductReviews — the community reviews block that lands on the PDP.
 *
 * Powered by /api/community/reviews (created in the previous sprint). Every
 * write auto-recomputes the product's SibaScore review component, closing the
 * "trust engine" loop on the shop.
 */
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Star, ThumbsUp, ThumbsDown, Flag, Sparkles, Loader2, CheckCircle2, ShieldAlert, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

interface Review {
  review_id: string;
  product_id: string;
  author_name: string;
  rating: number;
  title?: string | null;
  body: string;
  verified_purchase: boolean;
  upvotes: number;
  downvotes: number;
  flagged: boolean;
  created_at: string;
}

interface ReviewsPayload {
  items: Review[];
  total: number;
  average_rating: number;
  distribution: Record<string, number>;
  has_more?: boolean;
  offset?: number;
  limit?: number;
}

type SortMode = "top" | "recent";
const PAGE_SIZE = 10;

export const ProductReviews: React.FC<{ productId: string }> = ({ productId }) => {
  const auth = useAuth();
  const qc = useQueryClient();
  const [rating, setRating] = React.useState(5);
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [sortMode, setSortMode] = React.useState<SortMode>("top");
  const [pageIndex, setPageIndex] = React.useState(0);

  React.useEffect(() => { setPageIndex(0); }, [sortMode]);

  const q = useQuery<ReviewsPayload>({
    queryKey: ["reviews", productId, sortMode, pageIndex],
    queryFn: async () => (await api.get(`/community/reviews/${productId}`, {
      params: { sort: sortMode, limit: PAGE_SIZE, offset: pageIndex * PAGE_SIZE },
    })).data,
  });

  const create = useMutation({
    mutationFn: async () => (await api.post("/community/reviews", {
      product_id: productId, rating, title: title.trim() || null,
      body: body.trim(),
    })).data,
    onSuccess: () => {
      setTitle(""); setBody(""); setRating(5);
      qc.invalidateQueries({ queryKey: ["reviews", productId] });
    },
  });

  const vote = useMutation({
    mutationFn: async (v: { id: string; up: boolean }) =>
      (await api.post(`/community/reviews/${v.id}/vote`, { up: v.up })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reviews", productId] }),
  });

  const flag = useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/community/reviews/${id}/flag`, { reason: "Community flag" })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reviews", productId] }),
  });

  // Ban-recovery: quietly check if the signed-in user is banned from reviewing.
  // If yes we swap the "Write a review" block for the appeal flow.
  const banStatus = useQuery<{ banned: boolean; reason?: string; banned_at?: string; appeal?: { appeal_id: string; status: string } | null }>({
    queryKey: ["review-ban-status"],
    enabled: !!auth.user,
    queryFn: async () => (await api.get("/community/ban-status")).data,
    staleTime: 60_000,
  });

  const data = q.data;
  // Server-driven ordering + pagination. No client-side sort needed.
  const items = data?.items || [];

  const avg = data?.average_rating ?? 0;
  const total = data?.total ?? 0;
  const dist = data?.distribution || {};
  const maxDist = Math.max(1, ...Object.values(dist).map((v) => Number(v)));

  return (
    <section
      id="reviews"
      data-testid="pdp-reviews-section"
      className="rounded-2xl bg-white border border-[color:var(--sg-border)] p-6 mb-12"
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight text-[color:var(--sg-fg)]">Community reviews</h2>
          <div className="mt-1 flex items-center gap-2 text-sm text-[color:var(--sg-muted)]">
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className={`h-4 w-4 ${n <= Math.round(avg) ? "fill-[color:var(--sb-saffron,#F97316)] text-[color:var(--sb-saffron,#F97316)]" : "text-slate-300"}`} strokeWidth={2}/>
              ))}
            </div>
            <span className="tabular-nums font-semibold text-[color:var(--sg-fg)]">{avg.toFixed(1)}</span>
            <span>· {total} review{total === 1 ? "" : "s"}</span>
            <span className="mx-1">·</span>
            <span className="inline-flex items-center gap-1 text-[color:var(--sb-saffron,#F97316)] font-semibold">
              <Sparkles className="h-3 w-3" strokeWidth={2.5}/> Feeds SibaScore in real time
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 text-xs">
          {(["top", "recent"] as SortMode[]).map((m) => (
            <button
              key={m}
              onClick={() => setSortMode(m)}
              data-testid={`pdp-reviews-sort-${m}`}
              className={`px-3 py-1 rounded-full font-semibold ${sortMode === m ? "bg-[color:var(--sg-fg)] text-white" : "text-[color:var(--sg-muted)] hover:bg-[color:var(--sg-muted-bg)]"}`}
            >
              {m === "top" ? "Top voted" : "Most recent"}
            </button>
          ))}
        </div>
      </div>

      {/* Distribution histogram */}
      {total > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 mb-6">
          <div className="space-y-1.5">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = Number(dist[String(star)] || 0);
              const pct = (count / maxDist) * 100;
              return (
                <div key={star} className="flex items-center gap-2" data-testid={`pdp-reviews-dist-${star}`}>
                  <span className="text-xs w-8 text-[color:var(--sg-muted)] tabular-nums">{star}★</span>
                  <div className="flex-1 h-2 rounded-full bg-[color:var(--sg-muted-bg)] overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[color:var(--sb-saffron,#F97316)] to-fuchsia-400" style={{ width: `${Math.max(2, pct)}%` }}/>
                  </div>
                  <span className="text-xs w-8 text-right text-[color:var(--sg-muted)] tabular-nums">{count}</span>
                </div>
              );
            })}
          </div>
          <div className="rounded-xl border border-dashed border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 p-4 text-sm text-[color:var(--sg-fg)]">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--sg-muted)] mb-1">Why reviews matter here</div>
            Every review updates the product's SibaScore review component within seconds. Flag fake reviews and the moderation queue picks them up — the trust engine is community-driven.
          </div>
        </div>
      )}

      {/* Write review — or appeal flow if banned */}
      {auth.user && banStatus.data?.banned ? (
        <BanAppealBanner
          reason={banStatus.data.reason}
          existingAppeal={banStatus.data.appeal || null}
          onSubmitted={() => qc.invalidateQueries({ queryKey: ["review-ban-status"] })}
        />
      ) : auth.user ? (
        <div className="rounded-xl border border-[color:var(--sg-border)] bg-white p-4 mb-6" data-testid="pdp-reviews-write">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--sg-muted)] mb-2">Write a review</div>
          <div className="flex items-center gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setRating(n)}
                aria-label={`${n} star`}
                data-testid={`pdp-reviews-star-${n}`}
                className={`h-8 w-8 rounded-md ${n <= rating ? "text-[color:var(--sb-saffron,#F97316)]" : "text-slate-300"} hover:scale-110 transition-transform`}
              >
                <Star className={`h-6 w-6 mx-auto ${n <= rating ? "fill-current" : ""}`} strokeWidth={2}/>
              </button>
            ))}
            <span className="ml-2 text-xs text-[color:var(--sg-muted)]">Rated {rating}/5</span>
          </div>
          <Input
            data-testid="pdp-reviews-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Short headline (optional)"
            maxLength={140}
            className="mb-2 h-10 bg-white"
          />
          <Textarea
            data-testid="pdp-reviews-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Tell fellow shoppers what makes this product worth (or not worth) buying."
            rows={3}
            className="bg-white"
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[11px] text-[color:var(--sg-muted)]">Your review contributes to SibaScore instantly.</span>
            <Button
              data-testid="pdp-reviews-submit"
              onClick={() => create.mutate()}
              disabled={create.isPending || body.trim().length < 5}
              className="bg-[color:var(--sb-saffron,#F97316)] text-white hover:bg-[color:var(--sb-saffron-hover,#EA580C)] h-10 font-semibold"
            >
              {create.isPending
                ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> Posting…</>
                : create.isSuccess
                  ? <><CheckCircle2 className="h-4 w-4 mr-1.5"/> Posted</>
                  : <><Sparkles className="h-4 w-4 mr-1.5"/> Post review</>}
            </Button>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-[color:var(--sg-border)] p-6 text-center text-sm text-[color:var(--sg-muted)] mb-6">
          <a href="/login" className="font-semibold text-[color:var(--sb-saffron,#F97316)] hover:underline">Sign in</a> to write a review — every review moves the SibaScore in real time.
        </div>
      )}

      {/* Review list */}
      <div className="space-y-3" data-testid="pdp-reviews-list">
        {q.isLoading && <div className="text-sm text-[color:var(--sg-muted)]">Loading reviews…</div>}
        {items.length === 0 && !q.isLoading && (
          <div className="rounded-xl border border-dashed border-[color:var(--sg-border)] p-8 text-center text-sm text-[color:var(--sg-muted)]">
            No reviews yet. Be the first — your review directly nudges the SibaScore.
          </div>
        )}
        {items.map((r) => (
          <article key={r.review_id} className="rounded-xl border border-[color:var(--sg-border)] bg-white p-4" data-testid={`pdp-review-${r.review_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`h-3.5 w-3.5 ${n <= r.rating ? "fill-[color:var(--sb-saffron,#F97316)] text-[color:var(--sb-saffron,#F97316)]" : "text-slate-300"}`} strokeWidth={2}/>
                    ))}
                  </div>
                  <span className="text-xs font-semibold text-[color:var(--sg-fg)]">{r.author_name}</span>
                  {r.verified_purchase && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-500/10 px-2 py-0.5 rounded-full">Verified buyer</span>
                  )}
                  {r.flagged && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-500/10 px-2 py-0.5 rounded-full">Under review</span>
                  )}
                </div>
                {r.title && <div className="mt-1 font-semibold text-[color:var(--sg-fg)]">{r.title}</div>}
                <p className="mt-1 text-sm text-[color:var(--sg-fg)] leading-relaxed whitespace-pre-line">{r.body}</p>
                <div className="mt-2 text-[11px] text-[color:var(--sg-muted)]">{new Date(r.created_at).toLocaleDateString()}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1">
              <button
                onClick={() => vote.mutate({ id: r.review_id, up: true })}
                data-testid={`pdp-review-upvote-${r.review_id}`}
                className="inline-flex items-center gap-1 text-xs text-[color:var(--sg-muted)] hover:text-emerald-600 px-2 py-1 rounded-md hover:bg-emerald-500/10"
              >
                <ThumbsUp className="h-3.5 w-3.5"/> {r.upvotes}
              </button>
              <button
                onClick={() => vote.mutate({ id: r.review_id, up: false })}
                data-testid={`pdp-review-downvote-${r.review_id}`}
                className="inline-flex items-center gap-1 text-xs text-[color:var(--sg-muted)] hover:text-rose-600 px-2 py-1 rounded-md hover:bg-rose-500/10"
              >
                <ThumbsDown className="h-3.5 w-3.5"/> {r.downvotes}
              </button>
              <button
                onClick={() => flag.mutate(r.review_id)}
                data-testid={`pdp-review-flag-${r.review_id}`}
                className="ml-auto inline-flex items-center gap-1 text-xs text-[color:var(--sg-muted)] hover:text-amber-700 px-2 py-1 rounded-md hover:bg-amber-500/10"
              >
                <Flag className="h-3.5 w-3.5"/> Flag
              </button>
            </div>
          </article>
        ))}
      </div>

      {/* Pagination */}
      {data && data.total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-sm" data-testid="pdp-reviews-pagination">
          <span className="text-[color:var(--sg-muted)]">
            Showing {(pageIndex * PAGE_SIZE) + 1}–{Math.min((pageIndex + 1) * PAGE_SIZE, data.total)} of {data.total}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
              disabled={pageIndex === 0}
              data-testid="pdp-reviews-prev"
              className="px-3 py-1.5 rounded-md text-sm font-semibold border border-[color:var(--sg-border)] disabled:opacity-40 hover:bg-[color:var(--sg-muted-bg)]"
            >
              ← Prev
            </button>
            <button
              onClick={() => setPageIndex((p) => p + 1)}
              disabled={!data.has_more}
              data-testid="pdp-reviews-next"
              className="px-3 py-1.5 rounded-md text-sm font-semibold border border-[color:var(--sg-border)] disabled:opacity-40 hover:bg-[color:var(--sg-muted-bg)]"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

// ─── Ban Recovery ───────────────────────────────────────────────────────────

const BanAppealBanner: React.FC<{
  reason?: string | null;
  existingAppeal: { appeal_id: string; status: string } | null;
  onSubmitted: () => void;
}> = ({ reason, existingAppeal, onSubmitted }) => {
  const [text, setText] = React.useState("");
  const already = existingAppeal?.status === "pending" || existingAppeal?.status === "resolved";

  const submit = useMutation({
    mutationFn: async () => (await api.post("/community/appeal", { reason: text.trim() })).data,
    onSuccess: () => {
      toast.success("Appeal submitted — the moderation team will review within 48h.");
      setText("");
      onSubmitted();
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || "Could not submit appeal"),
  });

  return (
    <div
      data-testid="pdp-ban-appeal"
      className="rounded-xl border-2 border-rose-300/60 bg-gradient-to-br from-rose-50 to-amber-50 dark:from-rose-950/30 dark:to-amber-950/20 p-5 mb-6"
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 shrink-0 rounded-lg bg-rose-500/15 flex items-center justify-center text-rose-700 dark:text-rose-300">
          <ShieldAlert className="h-4 w-4" strokeWidth={2.5}/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700 dark:text-rose-300">
            Reviewing paused
          </div>
          <div className="mt-1 font-serif-display text-lg font-bold text-[color:var(--sg-fg)]">
            You can't post reviews right now
          </div>
          <p className="mt-1 text-sm text-[color:var(--sg-fg)]/80">
            {reason ? <>Reason from the moderation team: <b>{reason}</b>.</> : "The moderation team paused reviewing on your account."} {" "}
            You can still shop, wishlist, and compare — and appeal below to get reviewing back.
          </p>

          {existingAppeal?.status === "resolved" ? (
            <div className="mt-3 rounded-lg bg-emerald-500/10 border border-emerald-400/40 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4"/> Appeal resolved — refresh in a minute and try posting again.
            </div>
          ) : existingAppeal?.status === "pending" ? (
            <div className="mt-3 rounded-lg bg-amber-500/10 border border-amber-400/40 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
              Your appeal <code className="font-mono">{existingAppeal.appeal_id}</code> is being reviewed. Typical turnaround is under 48 hours. You can update the note below anytime.
            </div>
          ) : null}

          <Textarea
            data-testid="pdp-ban-appeal-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Tell the moderation team what happened. Be specific — the more context, the faster the review."
            rows={3}
            className="mt-3 bg-white dark:bg-slate-900/60"
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[11px] text-[color:var(--sg-muted)]">
              We reply within 48 hours. Approved appeals restore your reviews automatically.
            </span>
            <Button
              data-testid="pdp-ban-appeal-submit"
              onClick={() => submit.mutate()}
              disabled={submit.isPending || text.trim().length < 10}
              className="bg-rose-600 hover:bg-rose-700 text-white h-9 font-semibold"
            >
              {submit.isPending
                ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> Sending…</>
                : already
                  ? <><Send className="h-4 w-4 mr-1.5"/> Update appeal</>
                  : <><Send className="h-4 w-4 mr-1.5"/> Submit appeal</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
